/** Shim for `react-native-pdf`, rendered with pdf.js for reliable touch input. */
import React from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { toCss } from '../web/styleUtils';

export interface PdfProps {
  source?: { uri?: string; cache?: boolean } | number;
  style?: any;
  onLoadComplete?: (numberOfPages: number) => void;
  onError?: (error: any) => void;
  [key: string]: any;
}

GlobalWorkerOptions.workerSrc = workerSrc;

const Pdf = function Pdf({ source, style, onLoadComplete, onError }: PdfProps) {
  const uri = typeof source === 'object' ? source?.uri ?? '' : '';
  const [pdfData, setPdfData] = React.useState<Uint8Array | null>(null);
  const [pageImages, setPageImages] = React.useState<string[]>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const zoomRef = React.useRef(1);
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const pinchStartDistanceRef = React.useRef<number | null>(null);
  const pinchStartZoomRef = React.useRef(1);

  React.useEffect(() => {
    if (!uri) {
      setPdfData(null);
      return;
    }

    const prefix = 'data:application/pdf;base64,';
    if (!uri.startsWith(prefix)) {
      fetch(uri)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Unable to fetch PDF (${response.status})`);
          }
          return response.arrayBuffer();
        })
        .then((buffer) => setPdfData(new Uint8Array(buffer)))
        .catch((error) => {
          onError?.(error);
          setPdfData(null);
        });
      return;
    }

    try {
      const binary = atob(uri.slice(prefix.length));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      setPdfData(bytes);
    } catch (error) {
      onError?.(error);
      setPdfData(null);
    }
  }, [uri, onError]);

  React.useEffect(() => {
    let cancelled = false;

    const renderPdf = async () => {
      if (!pdfData?.length) {
        setPageImages([]);
        setPageCount(0);
        return;
      }

      try {
        const loadingTask = getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        setPageCount(pdf.numPages);
        onLoadComplete?.(pdf.numPages);

        const renderedPages: string[] = [];
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1.35 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: context, viewport }).promise;
          renderedPages.push(canvas.toDataURL('image/png'));
        }

        if (!cancelled) setPageImages(renderedPages);
      } catch (error) {
        if (!cancelled) onError?.(error);
      }
    };

    void renderPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfData, onError, onLoadComplete]);

  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const clampZoom = (value: number) => Math.min(3, Math.max(1, value));
    const getTouchDistance = (touches: TouchList) => {
      if (touches.length < 2) return 0;
      const first = touches[0];
      const second = touches[1];
      return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        pinchStartDistanceRef.current = getTouchDistance(event.touches);
        pinchStartZoomRef.current = zoomRef.current;
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !pinchStartDistanceRef.current) return;
      event.preventDefault();
      const distance = getTouchDistance(event.touches);
      const nextZoom = clampZoom((distance / pinchStartDistanceRef.current) * pinchStartZoomRef.current);
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
    };

    const onTouchEnd = () => {
      pinchStartDistanceRef.current = null;
      pinchStartZoomRef.current = zoomRef.current;
    };

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const nextZoom = clampZoom(zoomRef.current * (event.deltaY < 0 ? 1.08 : 0.92));
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
    };

    host.addEventListener('touchstart', onTouchStart, { passive: false });
    host.addEventListener('touchmove', onTouchMove, { passive: false });
    host.addEventListener('touchend', onTouchEnd, { passive: false });
    host.addEventListener('touchcancel', onTouchEnd, { passive: false });
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      host.removeEventListener('touchstart', onTouchStart);
      host.removeEventListener('touchmove', onTouchMove);
      host.removeEventListener('touchend', onTouchEnd);
      host.removeEventListener('touchcancel', onTouchEnd);
      host.removeEventListener('wheel', onWheel);
    };
  }, [pdfData]);

  if (!pdfData) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: '#666',
          fontSize: 14,
          background: '#fff',
          width: '100%',
          height: '100%',
          minHeight: 400,
          ...(toCss(style) as any),
        }}
      >
        No PDF content available for this shift log.
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      style={{
        position: 'relative',
        flex: 1,
        width: '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        alignSelf: 'stretch',
        overflowX: 'auto',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x pan-y',
        background: '#fff',
        ...(toCss(style) as any),
      }}
    >
      <div
        style={{
          minHeight: '100%',
          width: `${zoom * 100}%`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          margin: '0 auto',
        }}
      >
        {pageImages.map((pageImage, index) => (
          <img
            key={`${index}-${pageCount}`}
            src={pageImage}
            alt={`PDF page ${index + 1}`}
            draggable={false}
            style={{ width: '100%', height: 'auto', display: 'block', background: '#fff' }}
          />
        ))}
      </div>
    </div>
  );
};

export default Pdf;
